import { RAMP_DOWN_THRESHOLD, RAMP_UP_THRESHOLD, RAMP_WINDOW } from '@/constants/app';
import type { CategoryLevel, Difficulty, DifficultyOverride, Item } from './types';

/**
 * LOCKED difficulty ramp. Auto starts a category at level 1, climbs when at
 * least 80% of the last eight answers at the current level were right, and
 * drops when 40% or fewer were.
 */
export interface RampResult {
  level: Difficulty;
  changed: boolean;
  recentAtLevel: boolean[];
}

export function nextLevel(
  level: Difficulty,
  recentAtLevel: boolean[],
  correct: boolean,
): RampResult {
  const recent = [...recentAtLevel, correct].slice(-RAMP_WINDOW);

  if (recent.length < RAMP_WINDOW) return { level, changed: false, recentAtLevel: recent };

  const rate = recent.filter(Boolean).length / recent.length;

  if (rate >= RAMP_UP_THRESHOLD && level < 3) {
    return { level: (level + 1) as Difficulty, changed: true, recentAtLevel: [] };
  }
  if (rate <= RAMP_DOWN_THRESHOLD && level > 1) {
    return { level: (level - 1) as Difficulty, changed: true, recentAtLevel: [] };
  }
  return { level, changed: false, recentAtLevel: recent };
}

export function levelFor(
  category: string,
  levels: Map<string, CategoryLevel>,
  override: DifficultyOverride,
): Difficulty | 'all' {
  if (override === 'all') return 'all';
  if (override !== 'auto') return override;
  return levels.get(category)?.level ?? 1;
}

/**
 * Filters a pool to the wanted difficulty. If a category has nothing at that
 * level, the nearest level that does have items is used instead of handing back
 * an empty queue.
 */
export function filterToLevel(items: Item[], level: Difficulty | 'all'): Item[] {
  if (level === 'all') return items;
  const byCategory = new Map<string, Item[]>();
  for (const item of items) {
    const list = byCategory.get(item.category) ?? [];
    list.push(item);
    byCategory.set(item.category, list);
  }

  const out: Item[] = [];
  for (const list of byCategory.values()) {
    out.push(...list.filter((i) => i.difficulty === nearestLevelWithItems(list, level)));
  }
  // Keep the pool's original ordering.
  const keep = new Set(out.map((i) => i.id));
  return items.filter((i) => keep.has(i.id));
}

export function nearestLevelWithItems(items: Item[], wanted: Difficulty): Difficulty {
  const available = new Set(items.map((i) => i.difficulty));
  if (available.has(wanted)) return wanted;
  const candidates: Difficulty[] = [1, 2, 3];
  let best: Difficulty = wanted;
  let bestDistance = Number.MAX_SAFE_INTEGER;
  for (const level of candidates) {
    if (!available.has(level)) continue;
    const distance = Math.abs(level - wanted);
    // On a tie, prefer the easier level — dropping back is kinder than jumping up.
    if (distance < bestDistance || (distance === bestDistance && level < best)) {
      best = level;
      bestDistance = distance;
    }
  }
  return best;
}
